import {
  createRouter,
  createWebHistory,
  type NavigationGuardWithThis,
  type RouteLocationNormalized,
  type RouteRecordRaw
} from 'vue-router'
import { captureException, captureMessage } from '@sentry/browser'
import { authGuard } from '@auth0/auth0-vue'
import { useActorStore } from '@/stores/actor'
import { ToastProgrammatic } from '@ntohq/buefy-next'
import { i18n } from '@/config/i18n'

const isAuthGuard: NavigationGuardWithThis<undefined> = async (to: RouteLocationNormalized) => {
  return await authGuard(to)
}

const isActorPermissioned: NavigationGuardWithThis<undefined> = async (
  to: RouteLocationNormalized
) => {
  const actorStore = useActorStore()
  let currentActor = actorStore.getCurrentActor
  try {
    if (!currentActor) {
      await actorStore.initialize()
      currentActor = actorStore.getCurrentActor
    }
  } catch (error) {
    console.error(
      "There was an error refreshing the current user's information from local or remote systems"
    )
    captureException(error)
    return { name: 'error' }
  }
  if (!currentActor) {
    const msg = 'When navigating to a protected route, the user was not able to be authenticated'
    console.warn(msg)
    captureMessage(msg)
    return { name: 'error' }
  }
  if (!actorStore.hasAccess(to.meta.isPermissions as string[] | undefined)) {
    const msg = i18n.global.t('unauthorized-route-access', { route: to.name })
    console.warn(msg)
    // @ts-ignore
    ToastProgrammatic.prototype.open({
      message: msg,
      duration: 4000,
      type: 'is-warning'
    })
    return { name: 'home' }
  }
  return true
}

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: {
      isAuth: false
    }
  },
  {
    path: '/error',
    name: 'error',
    component: () => import('@/views/ErrorView.vue')
  },
  {
    path: '/sso-error',
    name: 'userLinkError',
    component: () => import('@/views/UserLinkErrorView.vue')
  },
  {
    path: '/callback',
    name: 'public-callback',
    component: () => import('@/views/CallbackView.vue'),
    meta: {
      isAuth: false
    }
  },
  {
    path: '/dashboard',
    redirect: { name: 'clinician-dashboard' },
    name: 'dashboard-root',
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/dashboard/clinicians',
    name: 'clinician-dashboard',
    component: () => import('@/views/DashboardView.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/clinicians/npi/:npi',
    name: 'clinician-detail',
    component: () => import('@/views/ClinicianOverview.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/search/clinicians/npi/:npi',
    name: 'clinician-search-detail',
    component: () => import('@/views/SearchClinicianOverview.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/saved-jobs/:jobId/clinicians/npi/:npi',
    name: 'clinician-jobs-detail',
    component: () => import('@/views/JobClinicianOverview.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/clinician-lookup/clinicians/npi/:npi',
    name: 'clinician-lookup-detail',
    component: () => import('@/views/ClinicianOverview.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/clinician-lookup',
    name: 'clinician-lookup',
    component: () => import('@/views/ClinicianSearch.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/saved-searches',
    name: 'saved-searches',
    component: () => import('@/views/SavedSearches.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/saved-jobs/welcome',
    name: 'job-welcome',
    component: () => import('@/views/JobWelcome.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/saved-jobs',
    name: 'we-jobs',
    component: () => import('@/views/JobsList.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/saved-jobs/:jobId',
    name: 'we-job-detail',
    component: () => import('@/views/JobDetail.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/views/Settings.vue'),
    beforeEnter: [isAuthGuard, isActorPermissioned],
    meta: {
      isAuth: true,
      isPermissions: ['get_workforce_intel']
    }
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/views/Logout.vue')
  },
  {
    path: '/expired',
    name: 'expired',
    component: () => import('@/views/Expired.vue')
  }
]
const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes
})

/*
router.afterEach((to, from) => {
  const toDepth = to.path.split('/').length
  const fromDepth = from.path.split('/').length
  to.meta.transition = toDepth < fromDepth ? 'slide-right' : 'slide-left'
})
*/

export default router

// This can be directly added to any of your `.ts` files like `router.ts`
// It can also be added to a `.d.ts` file. Make sure it's included in
// project's tsconfig.json "files"
import 'vue-router'

// To ensure it is treated as a module, add at least one `export` statement
export {}

declare module 'vue-router' {
  interface RouteMeta {
    // is optional
    isAuth?: boolean
    // must be declared by every route
    isPermissions?: string[]
    transition?: string
  }
}
